import { z } from 'zod';
/**
 * Eşleştirme kaydı — uygulamanın ana işi.
 * İki barkod arka arkaya okutulur: codeA (HJ/paket barkodu) ve codeB (ürün kodu).
 */
export const recordSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    templateId: z.string().uuid().nullable(),
    codeA: z.string().nullable(),
    codeB: z.string().nullable(),
    /** Dinamik form cevapları: { [fieldId]: değer }. Form kullanılmıyorsa boş. */
    data: z.record(z.unknown()),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
    createdBy: z.string().uuid(),
    createdByName: z.string(),
    /** Gerçek zaman damgası. Eski projede string'di, tarih aramaları bu yüzden yavaştı. */
    createdAt: z.string().datetime(),
});
/**
 * Bir eşleştirmeyi kaydeder. İki barkod da tek istekte gider — eski projedeki
 * gibi okuma başına ayrı sorgu yapılmaz.
 */
/**
 * "Ürün kodu yok" için kullanılan nöbet değeri.
 *
 * İade akışında bazı gönderilerin ürün kodu bulunmuyor; operatör düğmeye
 * basınca ürün kodu alanına bu yazılır. Veritabanındaki tekillik indeksi bu
 * değeri dışarıda bırakır — aksi halde ikinci "ürün kodu yok" kaydı çakışırdı.
 */
export const URUN_KODU_YOK = 'YOK';
export const createRecordSchema = z.object({
    templateId: z.string().uuid().optional(),
    codeA: z.string().trim().min(1),
    codeB: z.string().trim().min(1),
    data: z.record(z.unknown()).default({}),
    lat: z.number().optional(),
    lng: z.number().optional(),
    /**
     * Client tarafında üretilir. Aynı eşleştirme ağ hatası yüzünden iki kez
     * gönderilirse sunucu ikinciyi yok sayar — çift kayıt oluşmaz.
     */
    clientUuid: z.string().uuid(),
    /**
     * true ise mevcut bir eşleştirmenin üzerine yazılır. Client çakışma
     * uyarısını kullanıcıya gösterip onay aldıktan sonra tekrar gönderir.
     */
    overwrite: z.boolean().default(false),
});
/**
 * Eşleştirmesiz kayıt (barkodsuz ürün).
 *
 * Üç alan da isteğe bağlı görünüyor ama boş kayıt anlamsız: ürün kodu yoksa
 * elde tek tanımlayıcı görseldir, o yüzden en az birinin dolu olması şart.
 * Kural sunucuda da uygulanıyor — istemci doğrulaması atlanabilir.
 */
export const createEntrySchema = z
    .object({
    code: z.string().trim().min(1).optional(),
    aciklama: z.string().trim().max(500).optional(),
    /** `data:image/jpeg;base64,...` — istemci küçültüp gönderir. */
    image: z.string().startsWith('data:image/').optional(),
    clientUuid: z.string().uuid(),
})
    .refine((v) => Boolean(v.code) || Boolean(v.image), {
    message: 'Ürün kodu girilmediyse görsel zorunludur',
    path: ['image'],
});
/** Eşleştirme çakıştığında dönen yanıt (HTTP 409). */
export const conflictKindSchema = z.enum([
    /** Aynı codeA + aynı codeB zaten kayıtlı — yapılacak bir şey yok. */
    'duplicate',
    /** codeA başka bir codeB ile eşleşmiş. */
    'code_a_taken',
    /** codeB başka bir codeA ile eşleşmiş. */
    'code_b_taken',
]);
export const recordConflictSchema = z.object({
    kind: conflictKindSchema,
    /** Çakışan mevcut kayıt — kullanıcıya "şununla eşleşmiş" demek için. */
    existing: recordSchema,
});
/**
 * Kaydetme yanıtı. `replaced` doluysa eski eşleştirme geçersiz kılınmış
 * (soft-delete) ve yerine yeni kayıt açılmıştır.
 *
 * Örnek: abc↔123 varken abc↔456 onaylanırsa, abc↔123 kaydı silinmiş
 * işaretlenir, abc↔456 yeni kayıt olarak açılır. Eski eşleştirmenin ne zaman
 * ve kim tarafından değiştirildiği geçmişte kalır — üzerine yazsaydık kaybolurdu.
 */
export const createRecordResponseSchema = z.object({
    record: recordSchema,
    replaced: recordSchema.nullable(),
});
/**
 * Bir kodun mevcut eşleştirmesini sorgular — İLK barkod okutulur okutulmaz
 * çalışır, ikinci okumayı beklemeden.
 *
 * Amaç: operatör "abc"yi okuttuğu anda "bu barkod daha önce 123 ile
 * eşleştirilmiş, devam edilsin mi?" uyarısını görsün; boşuna ikinci barkodu
 * okutmasın. Client bunu önce yerel önbellekten cevaplar (anında), gerekirse
 * sunucuya sorar.
 */
export const codeStatusSchema = z.object({
    code: z.string(),
    /** Hangi tarafa ait olduğu tespit edilebildiyse. */
    side: z.enum(['a', 'b']).nullable(),
    /** Bu kod zaten bir eşleştirmede kullanılıyorsa o kayıt. */
    existing: recordSchema.nullable(),
});
export const recordQuerySchema = z.object({
    /** YYYY-MM-DD. Verilmezse bugün. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    /** codeA veya codeB içinde arama. */
    q: z.string().trim().optional(),
    cursor: z.string().optional(),
    /** İade akışında alt kategoriye göre süzme. */
    kategori: z.string().trim().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Tek kod sorgulama sonucu. Eski `ara()` fonksiyonunun karşılığı. */
export const lookupResultSchema = z.object({
    found: z.boolean(),
    record: recordSchema.nullable(),
});
/** Okutulması beklenen barkod. Eski `rabibarkodlar/barkodlar` dizisinin satır hali. */
export const expectedBarcodeSchema = z.object({
    id: z.string().uuid(),
    barcode: z.string(),
    batch: z.string().nullable(),
    isScanned: z.boolean(),
});
/**
 * Gösterge paneli özeti.
 *
 * Brisa gibi doğrulama yapan kategorilerde asıl soru "kaç hata var";
 * Rabi gibi eşleştirme kategorilerinde "kaç benzersiz iş yapıldı".
 * İkisi de aynı özetten okunuyor.
 */
export const dashboardSchema = z.object({
    /** Referans listesinde karşılığı bulunmayan kayıtlar (LCW). */
    notInList: z.number().default(0),
    date: z.string(),
    /** O gün yapılan toplam okuma. */
    total: z.number().int(),
    /** Doğrulama kuralına uyanlar. */
    valid: z.number().int(),
    /** Kurala uymayanlar — Brisa'da "hatalı etiket". */
    invalid: z.number().int(),
    /** Benzersiz barkod sayısı (aynı kod birkaç kez okutulmuş olabilir). */
    uniqueCodeA: z.number().int(),
    uniqueCodeB: z.number().int(),
    /** Kullanıcı başına okuma. */
    byUser: z.array(z.object({ name: z.string(), count: z.number().int() })),
    /** Saat başına okuma — yoğunluk grafiği için. */
    byHour: z.array(z.object({ hour: z.number().int(), count: z.number().int() })),
});
//# sourceMappingURL=record.js.map